import { Controller } from "@hotwired/stimulus"
import RapidCaptureParser from "../lib/rapid_capture_parser"

// Token type → CSS classes for highlighted spans
const TOKEN_CLASSES = {
  youtrack: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded px-0.5',
  due: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded px-0.5',
  project_valid: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded px-0.5',
  assignee_valid: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded px-0.5',
  plain_date: 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full px-1.5 py-0.5 text-[11px]'
}

export default class extends Controller {
  static targets = [
    'input',
    'hiddenTitle',
    'hiddenYoutrack',
    'form',
    'preview'
  ]

  static values = {
    projects: Array,
    users: Array,
    youtrackBase: String
  }

  connect() {
    this.parser = new RapidCaptureParser({
      projects: this.projectsValue,
      users: this.usersValue
    })
    this._debounceTimer = null
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
  }

  onInput() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this._processInput()
    }, 150)
  }

  onKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.formTarget.requestSubmit()
    }
  }

  onSubmit() {
    const text = this.inputTarget.textContent
    const parsed = this.parser.parse(text)

    // Populate title and youtrack hidden fields (these don't have SearchableSelect components)
    if (this.hasHiddenTitleTarget) {
      this.hiddenTitleTarget.value = parsed.title
    }
    if (this.hasHiddenYoutrackTarget) {
      const base = this.youtrackBaseValue || ''
      this.hiddenYoutrackTarget.value = parsed.fields.youtrack_url
        ? `${base}/issue/${parsed.fields.youtrack_url}`
        : ''
    }

    // Update existing SearchableSelect hidden fields directly so we don't
    // need duplicate hidden inputs in the DOM (avoids last-wins ambiguity)
    const form = this.formTarget
    if (parsed.fields.project_tag) {
      this._updateSearchableSelect(form, 'project_tag', parsed.fields.project_tag)
    }
    if (parsed.fields.assignee_id !== null) {
      this._updateSearchableSelect(form, 'assignee_id', parsed.fields.assignee_id)
    }
    if (parsed.fields.due_date) {
      this._updateDateField(form, 'due_date', parsed.fields.due_date)
    }

    // Remove the empty state for the target quadrant (server turbo_stream
    // also attempts this, but it can fail when the form is inside a turbo frame)
    const quadrantSelect = form.querySelector('select[name="command_centre_task[quadrant]"]')
    if (quadrantSelect) {
      const emptyState = document.getElementById(`empty_${quadrantSelect.value}`)
      if (emptyState) emptyState.remove()
    }
  }

  // -------------------------------------------------------------------------
  // Private
  // -------------------------------------------------------------------------

  _processInput() {
    try {
      const text = this.inputTarget.textContent
      const parsed = this.parser.parse(text)

      if (parsed.tokens.length > 0) {
        const html = this._buildHighlightedHTML(text, parsed.tokens)
        const offset = this._saveCursorOffset()
        this.inputTarget.innerHTML = html
        this._restoreCursorOffset(offset)
      }

      this._updatePreview(parsed)
    } catch (e) {
      // silently catch parse/render errors to avoid disrupting input
    }
  }

  _updateSearchableSelect(form, fieldName, value) {
    // The SearchableSelectComponent renders:
    //   <input type="hidden" name="command_centre_task[field_name]" data-searchable-select-target="hiddenField">
    const hiddenField = form.querySelector(
      `input[type="hidden"][name="command_centre_task[${fieldName}]"][data-searchable-select-target="hiddenField"]`
    )
    if (hiddenField) {
      hiddenField.value = value
      // Also update the visible text input so the UI reflects the change.
      // The hiddenField is the first child of its searchable-select controller div.
      const controllerEl = hiddenField.closest('[data-controller~="searchable-select"]')
      if (controllerEl) {
        const textInput = controllerEl.querySelector('[data-searchable-select-target="input"]')
        if (textInput) {
          // Find matching label from option divs
          const matchingOption = controllerEl.querySelector(
            `[data-searchable-select-target="option"][data-value="${value}"]`
          )
          if (matchingOption) {
            textInput.value = matchingOption.dataset.label || matchingOption.textContent.trim()
          }
        }
      }
    }
  }

  _updateDateField(form, fieldName, value) {
    // DateFieldComponent renders a regular date input
    const dateField = form.querySelector(`input[name="command_centre_task[${fieldName}]"]`)
    if (dateField) {
      dateField.value = value
    }
  }

  // -------------------------------------------------------------------------
  // Highlighting
  // -------------------------------------------------------------------------

  _buildHighlightedHTML(text, tokens) {
    let html = ''
    let cursor = 0

    for (const token of tokens) {
      // Plain text before token
      if (token.start > cursor) {
        html += this._escapeHTML(text.slice(cursor, token.start))
      }

      const classes = this._classesForToken(token)
      if (classes) {
        html += `<span class="${classes}">${this._escapeHTML(token.raw)}</span>`
      } else {
        html += this._escapeHTML(token.raw)
      }

      cursor = token.end
    }

    // Remaining plain text
    if (cursor < text.length) {
      html += this._escapeHTML(text.slice(cursor))
    }

    return html
  }

  _classesForToken(token) {
    switch (token.type) {
      case 'youtrack':
        return TOKEN_CLASSES.youtrack
      case 'due':
        return TOKEN_CLASSES.due
      case 'project':
        return token.valid ? TOKEN_CLASSES.project_valid : null
      case 'assignee':
        return token.valid ? TOKEN_CLASSES.assignee_valid : null
      case 'plain_date':
        return TOKEN_CLASSES.plain_date
      default:
        return null
    }
  }

  _escapeHTML(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  // -------------------------------------------------------------------------
  // Preview
  // -------------------------------------------------------------------------

  _updatePreview(parsed) {
    if (!this.hasPreviewTarget) return

    const parts = []
    if (parsed.title) parts.push(`<span class="text-gray-700 dark:text-gray-300">${this._escapeHTML(parsed.title)}</span>`)
    if (parsed.fields.project_tag) {
      parts.push(`<span class="text-green-600 dark:text-green-400">#${this._escapeHTML(parsed.fields.project_tag)}</span>`)
    }
    if (parsed.fields.assignee_id !== null) {
      const user = this.usersValue.find(u => u.id === parsed.fields.assignee_id)
      const name = user ? user.firstName : String(parsed.fields.assignee_id)
      parts.push(`<span class="text-blue-600 dark:text-blue-400">@${this._escapeHTML(name)}</span>`)
    }
    if (parsed.fields.due_date) {
      parts.push(`<span class="text-orange-600 dark:text-orange-400">due: ${this._escapeHTML(parsed.fields.due_date)}</span>`)
    }
    if (parsed.fields.youtrack_url) {
      parts.push(`<span class="text-purple-600 dark:text-purple-400">${this._escapeHTML(parsed.fields.youtrack_url)}</span>`)
    }

    this.previewTarget.innerHTML = parts.join('<span class="text-gray-300 dark:text-gray-600 mx-1">·</span>')
  }

  // -------------------------------------------------------------------------
  // Cursor preservation
  // -------------------------------------------------------------------------

  _saveCursorOffset() {
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return 0
    const range = sel.getRangeAt(0)
    const preRange = range.cloneRange()
    preRange.selectNodeContents(this.inputTarget)
    preRange.setEnd(range.startContainer, range.startOffset)
    return preRange.toString().length
  }

  _restoreCursorOffset(offset) {
    const sel = window.getSelection()
    if (!sel) return
    const range = document.createRange()
    let currentOffset = 0
    const walker = document.createTreeWalker(this.inputTarget, NodeFilter.SHOW_TEXT)
    let node
    while ((node = walker.nextNode())) {
      if (currentOffset + node.length >= offset) {
        range.setStart(node, offset - currentOffset)
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
        return
      }
      currentOffset += node.length
    }
    // Fallback: place cursor at end
    range.selectNodeContents(this.inputTarget)
    range.collapse(false)
    sel.removeAllRanges()
    sel.addRange(range)
  }
}
